<template>
  <div class="chart-container">
    <div v-if="loading" class="chart-loading">
      <div class="spinner"></div>
    </div>
    <div v-else-if="noData" class="no-data">
      <i class="fas fa-chart-bar"></i>
      <p>Keine Testdaten verfügbar</p>
    </div>
    <Bar v-else :data="chartData" :options="chartOptions" />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { Bar } from 'vue-chartjs';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

// ChartJS-Komponenten registrieren
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default defineComponent({
  name: 'TestUsageChart',
  components: {
    Bar
  },
  props: {
    testData: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const loading = ref(true);

    // Prüfen, ob Daten vorhanden sind
    const noData = computed(() => !props.testData || props.testData.length === 0);

    // Chart-Daten berechnen
    const chartData = computed(() => {
      if (noData.value) {
        return {
          labels: [],
          datasets: []
        };
      }

      const sortedData = [...props.testData].sort((a, b) => b.count - a.count);
      
      return {
        labels: sortedData.map(item => item.name),
        datasets: [{
          label: 'Anzahl durchgeführter Tests',
          data: sortedData.map(item => item.count),
          backgroundColor: generateColors(sortedData.length),
          borderWidth: 1
        }]
      };
    });

    // Chart-Optionen
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.label}: ${context.raw} mal durchgeführt`;
            }
          }
        },
        title: {
          display: true,
          text: 'Durchgeführte Tests',
          color: '#333',
          font: {
            size: 16,
            weight: 'normal'
          },
          padding: {
            bottom: 20
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Anzahl',
            color: '#666',
            font: {
              size: 12
            }
          },
          ticks: {
            precision: 0
          }
        },
        x: {
          title: {
            display: true,
            text: 'Tests',
            color: '#666',
            font: {
              size: 12
            }
          }
        }
      }
    };

    // Generiert Farben für die Balken
    function generateColors(count) {
      const colors = [
        'rgba(59, 130, 246, 0.7)', // Blau
        'rgba(16, 185, 129, 0.7)', // Grün
        'rgba(245, 158, 11, 0.7)', // Orange
        'rgba(139, 92, 246, 0.7)', // Lila
        'rgba(14, 165, 233, 0.7)', // Hellblau
        'rgba(236, 72, 153, 0.7)'  // Pink
      ];
      
      const result = [];
      for (let i = 0; i < count; i++) {
        result.push(colors[i % colors.length]);
      }
      return result;
    }

    onMounted(() => {
      setTimeout(() => {
        loading.value = false;
      }, 600);
    });

    return {
      loading,
      noData,
      chartData,
      chartOptions
    };
  }
});
</script>

<style lang="scss" scoped>
.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.chart-loading, .no-data {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s ease-in-out infinite;
}

.no-data {
  i {
    font-size: 32px;
    color: #cbd5e1;
    margin-bottom: 12px;
  }
  
  p {
    color: #64748b;
    font-size: 14px;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style> 